import { useCallback } from 'react'

import { useStatsigClient } from '@statsig/react-bindings'

export enum FeatureGate {
  ApprovalConditions = 'approval_conditions',
  AicaApplicationAutoOpen = 'aica_application_auto_open',
  AicaRecordDetailAutoOpen = 'aica_record_detail_auto_open',
  AicaInsightsNotableQuestions = 'aica_insights_notable_questions',
  AiChatTopics = 'ai_chat_topics',
  AIAutomations = 'ai_automations',
  ApplicationAutoCheck = 'application_auto_check',
  AutoCheckEnablementBanners = 'auto_check_enablement_banners',
  AutoCheckSection = 'auto_check_section',
  CustomTaskFieldColumns = 'custom_task_field_columns',
  EnableTaskReminderEmails = 'enable_task_reminder_emails',
  EntityVersionMapMode = 'entity_version_map_mode',
  EntityVersionNearbyPropertiesReport = 'entity_version_nearby_properties_report',
  GISAutoFill = 'gis_autofill',
  GISPropertyColumns = 'gis_property_columns',
  InProductAICA = 'in_product_aica',
  InsightsAutoCheckPage = 'insights_auto_check_page',
  InsightsCommunityPage = 'insights_community_page',
  InsightsInspectionsPage = 'insights_inspections_page',
  // Some sections in the community insights page are restricted to specific orgs or GovWell staff
  InsightsCommunityRestrictedSections = 'insights_community_restricted_sections',
  InspectionCalendarExternalIntegration = 'inspection_calendar_external_integration',
  InspectionsAllowEditingLoggedInspections = 'inspections_allow-editing-logged-inspections',
  InternalStaffAI = 'internal_staff_ai',
  InternalStaffAIAnalyze = 'internal_staff_ai_analyze',
  ManualPaidByCapture = 'manual_paid_by_capture',
  NotificationSettingsTab = 'notification_settings_tab',
  ShowAutomaticGenerateToggle = 'show_automatic_generate_toggle',
  ShowAutomaticIssueToggle = 'show_automatic_issue_toggle',
  ShowAutomaticPaymentToggle = 'show_automatic_payment_toggle',
  ShowFeeProrateByMonthOption = 'show_fee_prorate_by_month_option',
  ShowInspectionsMobileAppBanner = 'show_inspections_mobile_app_banner',
  TimeBasedCitizenInspectionScheduling = 'time-based_citizen_inspection_scheduling',
  RenewalsOverhaul = 'renewals_overhaul',
  ReportingV2 = 'is_reporting_v2_enabled',
}

export const useFeatureFlagging = () => {
  const client = useStatsigClient()
  const checkFeatureFlag = useCallback(
    (featureFlag: FeatureGate) => {
      return client.checkGate(featureFlag)
    },
    [client]
  )
  return { checkFeatureFlag }
}

export const useIsInspectionCalendarExternalIntegrationEnabled = (): boolean => {
  const { checkFeatureFlag } = useFeatureFlagging()
  return checkFeatureFlag(FeatureGate.InspectionCalendarExternalIntegration)
}

export const useIsInternalStaffAIEnabled = (): boolean => {
  const { checkFeatureFlag } = useFeatureFlagging()
  return checkFeatureFlag(FeatureGate.InternalStaffAI)
}

export const useIsInternalStaffAIAnalyzeEnabled = (): boolean => {
  const { checkFeatureFlag } = useFeatureFlagging()
  return checkFeatureFlag(FeatureGate.InternalStaffAIAnalyze)
}

export const useIsInspectionsMobileAppBannerEnabled = (): boolean => {
  const { checkFeatureFlag } = useFeatureFlagging()
  return checkFeatureFlag(FeatureGate.ShowInspectionsMobileAppBanner)
}

export const useIsInProductAICAEnabled = (skip = false): boolean => {
  const { checkFeatureFlag } = useFeatureFlagging()
  if (skip) {
    return false
  }
  return checkFeatureFlag(FeatureGate.InProductAICA)
}

export const useIsRenewalsOverhaulEnabled = (): boolean => {
  const { checkFeatureFlag } = useFeatureFlagging()
  return checkFeatureFlag(FeatureGate.RenewalsOverhaul)
}

export const useIsApprovalConditionsEnabled = (): boolean => {
  const { checkFeatureFlag } = useFeatureFlagging()
  return checkFeatureFlag(FeatureGate.ApprovalConditions)
}

export const useIsReportingV2Enabled = (): boolean => {
  const { checkFeatureFlag } = useFeatureFlagging()
  return checkFeatureFlag(FeatureGate.ReportingV2)
}
